import React, { useState } from 'react';
import {
    Container,
    Box,
    Grid,
    Divider
} from '@mui/material';
import { Assessment } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { PortalScreenHeader } from '../../../../components/PortalScreenHeader';
import { TableControlBar } from '../../../../components/TableControlBar';
import { tablePreferencesStore } from '../../../../app/stores/TablePreferences';
import { productAnalyticsStore } from '../../../../app/stores/ProductAnalytics';
import TopProductsChart from './components/TopProductsChart';
import LowStockAlert from './components/LowStockAlert';
import SalesVsNonsalesChart from './components/SalesVsNonsalesChart';
import ProductViewsTable from './components/ProductViewsTable';
import ProductViewsSparkline from './components/ProductViewsSparkline';
import UniqueViewsSparkline from './components/UniqueViewsSparkline';
import SalesVsNonsalesSparkline from './components/SalesVsNonsalesSparkline';
import ProductSummaryStats from './components/ProductSummaryStats';
import CategoryPerformanceChart from './components/CategoryPerformanceChart';
import StockLevelDistribution from './components/StockLevelDistribution';
import PriceRangeDistribution from './components/PriceRangeDistribution';
import ProductActivitySummary from './components/ProductActivitySummary';
import ViewsStockCorrelation from './components/ViewsStockCorrelation';
import DiscountImpactAnalysis from './components/DiscountImpactAnalysis';
import ProductFreshnessScore from './components/ProductFreshnessScore';
import CategoryStockHealth from './components/CategoryStockHealth';

export type DateRange = [Moment, Moment];

const ProductAnalytics: React.FC = () => {
    // Get preferences from store
    const {
        analytics: analyticsPrefs,
        updateAnalyticsPreferences
    } = tablePreferencesStore();

    // Get store actions for refresh
    const {
        fetchTopProducts,
        fetchProductsWithViews,
        fetchSalesVsNonsales,
        fetchLowStockProducts,
        fetchLowestStockProducts
    } = productAnalyticsStore((state) => ({
        fetchTopProducts: state.fetchTopProducts,
        fetchProductsWithViews: state.fetchProductsWithViews,
        fetchSalesVsNonsales: state.fetchSalesVsNonsales,
        fetchLowStockProducts: state.fetchLowStockProducts,
        fetchLowestStockProducts: state.fetchLowestStockProducts
    }));

    const [dateRange, setDateRange] = useState<DateRange>([
        moment().subtract(30, 'days').startOf('day'),
        moment().endOf('day')
    ]);
    const [activeTimeFilter, setActiveTimeFilter] = useState<string>(analyticsPrefs.activeTimeFilter || '30d');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [categoryCount, setCategoryCount] = useState<number>(0);

    const handleQuickRange = (value: number, unit: 'hours' | 'days' | 'special') => {
        if (unit === 'special') {
            if (value === 0) {
                // Today
                setDateRange([moment().startOf('day'), moment().endOf('day')]);
            } else if (value === 1) {
                // This Month
                setDateRange([moment().startOf('month'), moment().endOf('month')]);
            } else if (value === 2) {
                // This Year
                setDateRange([moment().startOf('year'), moment().endOf('year')]);
            }
        } else if (unit === 'hours') {
            setDateRange([moment().subtract(value, 'hours'), moment()]);
        } else {
            setDateRange([moment().subtract(value, 'days').startOf('day'), moment().endOf('day')]);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const startDate = dateRange[0].toISOString();
            const endDate = dateRange[1].toISOString();

            // Refresh all analytics data in parallel
            await Promise.all([
                fetchTopProducts(10, startDate, endDate),
                fetchProductsWithViews(startDate, endDate),
                fetchSalesVsNonsales(startDate, endDate),
                fetchLowStockProducts(),
                fetchLowestStockProducts(10)
            ]);
        } catch (error) {
            console.error('Error refreshing analytics:', error);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <Container
            component='main'
            maxWidth='xl'
            sx={{
                height: '100%',
                paddingX: { xs: 0, md: 2 },
                pb: 8
            }}
        >
            <PortalScreenHeader
                title='Product Analytics'
                subtitle='Track product performance and sales metrics'
                icon={<Assessment />}
            />
            <Divider />

            {/* Time Filter Bar */}
            <TableControlBar
                showDenseToggle={false}
                showRefresh={true}
                isLoading={isRefreshing}
                onRefresh={handleRefresh}
                activeFilter={activeTimeFilter}
                filters={[
                    { label: '6 Hours', value: '6h' },
                    { label: '12 Hours', value: '12h' },
                    { label: '24 Hours', value: '24h' },
                    { label: '48 Hours', value: '48h' },
                    { label: '72 Hours', value: '72h' },
                    { label: 'Today', value: 'today' },
                    { label: 'Last 7 Days', value: '7d' },
                    { label: 'Last 14 Days', value: '14d' },
                    { label: 'Last 30 Days', value: '30d' },
                    { label: 'This Month', value: 'month' },
                    { label: 'This Year', value: 'year' }
                ]}
                onFilterChange={(value) => {
                    setActiveTimeFilter(value);
                    // Persist to store
                    updateAnalyticsPreferences({ activeTimeFilter: value });

                    switch (value) {
                        case '6h': handleQuickRange(6, 'hours'); break;
                        case '12h': handleQuickRange(12, 'hours'); break;
                        case '24h': handleQuickRange(24, 'hours'); break;
                        case '48h': handleQuickRange(48, 'hours'); break;
                        case '72h': handleQuickRange(72, 'hours'); break;
                        case 'today': handleQuickRange(0, 'special'); break;
                        case '7d': handleQuickRange(7, 'days'); break;
                        case '14d': handleQuickRange(14, 'days'); break;
                        case '30d': handleQuickRange(30, 'days'); break;
                        case 'month': handleQuickRange(1, 'special'); break;
                        case 'year': handleQuickRange(2, 'special'); break;
                    }
                }}
            />

            <Box sx={{ mt: 4 }}>
                <Grid container spacing={3}>
                    {/* Product Summary Stats - Full Width Row */}
                    <Grid item xs={12}>
                        <ProductSummaryStats dateRange={dateRange} />
                    </Grid>

                    {/* Sparkline Charts - Three in a row */}
                    <Grid item xs={12} md={4}>
                        <ProductViewsSparkline
                            title="Product Views"
                            subtitle={`Total views from ${dateRange[0].format('MMM D')} to ${dateRange[1].format('MMM D')}`}
                            dateRange={dateRange}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <UniqueViewsSparkline
                            title="Unique Views"
                            subtitle={`Unique visitor views trend`}
                            dateRange={dateRange}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <SalesVsNonsalesSparkline
                            title="Sales vs Regular"
                            subtitle="Views comparison by pricing"
                            dateRange={dateRange}
                        />
                    </Grid>

                    {/* Top Products Chart and Category Performance - Side by side if <= 15 categories */}
                    {categoryCount > 15 ? (
                        <>
                            <Grid item xs={12}>
                                <TopProductsChart limit={10} dateRange={dateRange} />
                            </Grid>
                            <Grid item xs={12}>
                                <CategoryPerformanceChart onDataLoad={setCategoryCount} />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} md={6}>
                                <TopProductsChart limit={10} dateRange={dateRange} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CategoryPerformanceChart onDataLoad={setCategoryCount} />
                            </Grid>
                        </>
                    )}

                    {/* Low Stock Alert - Left Half */}
                    <Grid item xs={12} md={6}>
                        <LowStockAlert />
                    </Grid>

                    {/* Sales vs Non-Sales Chart - Right Half */}
                    <Grid item xs={12} md={6}>
                        <SalesVsNonsalesChart dateRange={dateRange} />
                    </Grid>

                    {/* Stock Level Distribution - Left Half */}
                    <Grid item xs={12} md={6}>
                        <StockLevelDistribution />
                    </Grid>

                    {/* Price Range Distribution - Right Half */}
                    <Grid item xs={12} md={6}>
                        <PriceRangeDistribution />
                    </Grid>

                    {/* Product Activity Summary - Full Width */}
                    <Grid item xs={12}>
                        <ProductActivitySummary dateRange={dateRange} />
                    </Grid>

                    {/* Views vs Stock Correlation - Left Half */}
                    <Grid item xs={12} md={6}>
                        <ViewsStockCorrelation dateRange={dateRange} />
                    </Grid>

                    {/* Discount Impact Analysis - Right Half */}
                    <Grid item xs={12} md={6}>
                        <DiscountImpactAnalysis dateRange={dateRange} />
                    </Grid>

                    {/* Product Freshness Score - Left Half */}
                    <Grid item xs={12} md={6}>
                        <ProductFreshnessScore />
                    </Grid>

                    {/* Category Stock Health - Right Half */}
                    <Grid item xs={12} md={6}>
                        <CategoryStockHealth />
                    </Grid>

                    {/* Product Views Table - Full Width */}
                    <Grid item xs={12}>
                        <ProductViewsTable dateRange={dateRange} />
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default ProductAnalytics;

